import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/api';

interface AuthContextType {
  user: any;
  permissions: string[];
  loading: boolean; // Yeni loading durumu
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (resource: string, action?: string) => boolean;
  hasRole: (roleCode: string) => boolean;
  isAdmin: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  permissions: [],
  loading: true, // Varsayılan olarak true
  login: async () => {},
  logout: () => {},
  hasPermission: () => false,
  hasRole: () => false,
  isAdmin: false,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roleCodes, setRoleCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true); // Yeni loading durumu
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios
        .get('/auth/me')
        .then((response) => {
          console.log("Kullanıcı bilgileri:", response.data); // Debug için logla
          setUser(response.data.user);
          setPermissions(response.data.permissions || []);
          setRoleCodes(response.data.user?.roleCodes || []);
          setIsAdmin(response.data.user?.isAdmin || false);
        })
        .catch((err) => {
          console.error("Auth error:", err);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        })
        .finally(() => setLoading(false)); // Yükleme tamamlandı
    } else {
      setLoading(false); // Token yoksa yükleme tamamlandı
    }
  }, []);

  const login = async (username: string, password: string) => {
    const response = await axios.post('/auth/login', { username, password });
    const token = response.data.token;
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(response.data.user);
    setPermissions(response.data.permissions || []);
    setRoleCodes(response.data.user?.roleCodes || []);
    setIsAdmin(response.data.user?.isAdmin || false);
    navigate('/'); // Giriş başarılı olduğunda yönlendirme
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setPermissions([]);
    setRoleCodes([]);
    setIsAdmin(false);
    navigate('/login');
  };

  const hasPermission = (resource: string, action: string = 'read') => {
    // Admin her şeye erişebilir
    if (isAdmin) {
      console.log(`Admin check passed for: ${resource} - ${action}`);
      return true;
    }

    // Debug için
    console.log(`Permission check for: ${resource} - ${action}`, "Permissions:", permissions);
    
    // İzinleri kontrol et
    // permissions artık sadece string dizisi değil, resource ve actions içerebilen obje dizisi
    if (!Array.isArray(permissions)) return false;
    
    return permissions.some(p => {
      if (typeof p === 'string') {
        return p.toLowerCase() === resource.toLowerCase();
      }
      
      // Eğer p bir obje ise
      if (typeof p === 'object' && p !== null) {
        const permResource = p.resource || '';
        const permActions = Array.isArray(p.actions) ? p.actions : [];
        return permResource.toLowerCase() === resource.toLowerCase() && 
               (action === '*' || permActions.includes(action));
      }
      
      return false;
    });
  };

  // Rol koduna göre erişim kontrolü
  const hasRole = (roleCode: string) => {
    if (isAdmin) {
      console.log("Admin check passed for role:", roleCode);
      return true;
    }

    console.log("Role check for:", roleCode, "User roles:", roleCodes);
    
    return roleCodes.some(code => code === roleCode);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      permissions, 
      loading, 
      login, 
      logout, 
      hasPermission, 
      hasRole,
      isAdmin 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
